import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
const pricingTiers = [{
  id: 1,
  name: "Essential",
  price: "$7,500",
  description: "Perfect for small businesses with straightforward data",
  features: ["Up to 5 years of historical data", "Standard data mapping", "Basic audit-ready reports", "Email support", "2-week migration timeline"],
  recommended: false
}, {
  id: 2,
  name: "Professional",
  price: "$12,500",
  description: "Ideal for mid-sized organizations with complex requirements",
  features: ["Up to 10 years of historical data", "Advanced data mapping & validation", "Comprehensive audit-ready reports", "Branded cheque remittance templates", "Priority email & phone support", "3-week migration timeline", "Post-migration consultation (2 hours)"],
  recommended: true
}, {
  id: 3,
  name: "Enterprise",
  price: "Custom",
  description: "Tailored solutions for large-scale migrations",
  features: ["Unlimited historical data", "Custom data mapping & workflows", "Executive-level audit documentation", "Fully branded outputs & templates", "Dedicated migration manager", "24/7 support during migration", "Flexible timeline", "Post-migration training sessions", "Integration with third-party systems"],
  recommended: false
}];
export default function OurTransparentPricingStructure() {
  return <section className="bg-background py-16 lg:py-24 animate-fade-in-up">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16 space-y-4">
          <h2 className="font-display font-bold text-3xl lg:text-4xl text-foreground tracking-tight">
            Our Transparent Pricing Structure
          </h2>
          <p className="text-muted-dark text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            Scalable pricing tiers designed for your migration complexity. All
            pricing is in Canadian dollars (CAD).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {pricingTiers.map((tier, i) => <Card key={tier.id} data-index={i} className={`relative flex flex-col shadow-soft border border-border hover-lift ${tier.recommended ? "ring-2 ring-primary shadow-medium" : ""}`}>
              {tier.recommended && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-accent text-white px-4 py-1 rounded-full text-sm font-semibold shadow-md">
                  Recommended
                </div>}
              <CardHeader className="border-b border-border pb-6">
                <CardTitle className="text-2xl font-bold text-foreground">
                  {tier.name}
                </CardTitle>
                <CardDescription className="text-muted-dark mt-2">
                  {tier.description}
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-primary">
                    {tier.price}
                  </span>
                  {tier.price !== "Custom" && <span className="text-muted-dark text-sm ml-2">CAD</span>}
                </div>
              </CardHeader>
              <CardContent className="flex-1 pt-6">
                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3">
                      <Check className="size-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground leading-relaxed">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </CardContent>
              <CardFooter className="pt-6 border-t border-border">
                <Link to="/ContactPage" className="w-full">
                  <Button variant={tier.recommended ? "default" : "outline"} className="w-full h-12 font-semibold">
                    Get Started
                  </Button>
                </Link>
              </CardFooter>
            </Card>)}
        </div>

        <div className="bg-muted-light rounded-lg p-8 shadow-soft border border-border">
          <h3 className="font-display font-semibold text-xl text-foreground mb-4">
            What's Included in All Tiers
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold text-foreground">
                  Feature
                </TableHead>
                <TableHead className="text-center font-semibold text-foreground">
                  Essential
                </TableHead>
                <TableHead className="text-center font-semibold text-foreground">
                  Professional
                </TableHead>
                <TableHead className="text-center font-semibold text-foreground">
                  Enterprise
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">
                  Audit-ready reports
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">Data validation</TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">
                  Migration documentation
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
                <TableCell className="text-center">
                  <Check className="inline size-5 text-primary" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">
                  Post-migration support
                </TableCell>
                <TableCell className="text-center text-muted-dark">
                  30 days
                </TableCell>
                <TableCell className="text-center text-muted-dark">
                  60 days
                </TableCell>
                <TableCell className="text-center text-muted-dark">
                  90 days
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="mt-12 text-center">
          <p className="text-muted-dark text-sm mb-4">
            Need a custom solution or have questions about pricing?
          </p>
          <Link to="/ContactPage">
            <Button variant="secondary" size="lg">
              Contact Our Team
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}